import { Injectable } from '@angular/core';
import { BehaviorSubject, timer } from 'rxjs';
import { take } from 'rxjs/operators';

export interface NotificationAlert {
  type: 'success' | 'danger' | 'warning' | 'info' | 'notification';
  message: string;
}

@Injectable({
  providedIn: 'root',
})
export class AlertNotificationsService {
  private alertsSubject = new BehaviorSubject<NotificationAlert[]>([]);
  public alerts$ = this.alertsSubject.asObservable();

  constructor() { }

  // Accodamento centralizzato con deduplica difensiva: se un alert identico
  // (stesso tipo e stesso messaggio) è già attivo, non lo si accoda di nuovo.
  // Evita lo stack di toast identici quando arrivano più eventi ravvicinati.
  private pushAlert(alert: NotificationAlert, durationMs: number) {
    const currentAlerts = this.alertsSubject.value;
    const alreadyShown = currentAlerts.some(
      (a) => a.type === alert.type && a.message === alert.message
    );
    if (alreadyShown) {
      return;
    }

    this.alertsSubject.next([...currentAlerts, alert]);

    timer(durationMs)
      .pipe(take(1))
      .subscribe(() => {
        this.removeAlert(alert);
      });
  }

  private showNotification(
    messageKey: string,
    entityName?: string,
    type: 'success' | 'danger' | 'warning' | 'info' | 'notification' = 'info'
  ) {
    this.pushAlert({ type: type, message: messageKey }, 3000);
  }

  error(error?: any | string) {
    var message = '';
    if (typeof error === 'string') {
      message = error;
    } else if (error && error.error?.message) {
      message = error.error.message;
    } else {
      message = 'Something went wrong';
    }
    this.pushAlert({ type: 'danger', message: message }, 5000);
  }

  warning(message: string) {
    this.pushAlert({ type: 'warning', message: message }, 5000);
  }

  success(message: string) {
    this.showNotification(message, undefined, 'success');
  }

  info(message: string) {
    this.showNotification(message, undefined, 'info');
  }

  notification(message: string) {
    this.showNotification(message, undefined, 'notification');
  }

  create(entityName: string) {
    this.showNotification(`notifications.createSuccess`, entityName, 'success');
  }

  update(entityName: string) {
    this.showNotification(`notifications.updateSuccess`, entityName, 'success');
  }

  delete(entityName: string) {
    this.showNotification(`notifications.deleteSuccess`, entityName, 'success');
  }

  private removeAlert(alertToRemove: NotificationAlert) {
    const currentAlerts = this.alertsSubject.value;
    const filteredAlerts = currentAlerts.filter(
      (alert) => alert !== alertToRemove
    );
    this.alertsSubject.next(filteredAlerts);
  }
}
